import { useContext, useEffect } from 'react'
import { useParams, withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import { Form, FormAttrType, useEffectSkipFirst, useForm, useNestedForm } from 'utils/hooks'
import { Alert, Button, CloseButton, ProgressBar } from 'react-bootstrap'
import { GlobalStateContext } from 'contexts/global_state_context'
import { BooleanRadioField, CheckboxField, ChoiceField, DateField, InputField, MultipleSelectField, SelectField } from 'components/shared/input'
import { useState } from 'react'
import { Flex } from 'components/shared/flex'
import { useFetchInstitutionsApi } from 'api/institution'
import { UserFormView as PatientFormView } from 'components/patient/patient_form'
import { FaPlus } from 'react-icons/fa'
import { ThemeContext } from 'contexts/theme_context'
import { useFetchSamplesApi, usePutInputSampleApi } from 'api/sample'
import { useFetchProductItemsApi } from 'api/product_item'
import { ProductSearchForm } from 'entities/product'
import { useFetchDiseasesApi } from 'api/disease'
import { SampleInputForm, SampleSearchForm, SMOKING_STATUS_SELECT_ITEMS } from 'entities/sample'
import { useShowTransportationReportApi } from 'api/transportation_report'
import { PatientForm } from 'entities/patient'
import { TransportationReportViewModel } from 'view_models/transportation_report_view_model'
import { useFetchCouponsApi } from 'api/coupon'
import { Coupon, CouponSearchForm } from 'entities/coupon'
import { CouponCodeSearchForm } from 'entities/coupon_code'
import { useFetchCouponCodesApi } from 'api/coupon_code'
import { RequestFormErrorSearchForm } from 'entities/request_form_error'
import { useFetchRequestFormErrorsApi } from 'api/request_form_error'

type Props = {
    history: H.History
}

const InputPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const theme = useContext(ThemeContext)
    const params = useParams<{ id: string }>()
    const form = useForm<SampleInputForm>({
        patient: { address: {} },
        transportationReportId: Number(params.id),
        familyHistories: [{}],
    })
    const patientForm = useNestedForm<SampleInputForm, PatientForm>(form, 'patient')
    const institutionsApi = useFetchInstitutionsApi()
    const inputApi = usePutInputSampleApi()
    const showTransportationApi = useShowTransportationReportApi()
    const [showConfirm, setShowConfirm] = useState<boolean>(false)
    const productSearchForm = useForm<ProductSearchForm>({ perPage: 100000 })
    const productItemsApi = useFetchProductItemsApi(productSearchForm)
    const diseasesApi = useFetchDiseasesApi()
    const samplesSearchForm = useForm<SampleSearchForm>({ inspectionStatus: 'DRAFT' })
    const samplesApi = useFetchSamplesApi(samplesSearchForm)
    const couponsSearchForm = useForm<CouponSearchForm>({})
    const couponsApi = useFetchCouponsApi(couponsSearchForm)
    const errorsSearchForm = useForm<RequestFormErrorSearchForm>({})
    const errorsApi = useFetchRequestFormErrorsApi(errorsSearchForm)

    useEffect(() => {
        institutionsApi.execute()
        productItemsApi.execute()
        diseasesApi.execute()
        showTransportationApi.execute(Number(params.id))
        couponsApi.execute()
    }, [])

    useEffectSkipFirst(() => {
        globalState.setLoading(showTransportationApi.loading)
    }, [showTransportationApi.loading])

    useEffectSkipFirst(() => {
        if (samplesApi.isSuccess()) {
            if (samplesApi.response.results && samplesApi.response.results.length > 0) {
                const sample = samplesApi.response.results[0]
                form.set({
                    requestFormBarcode: form.object.requestFormBarcode,
                    preRequestFormBarcode: form.object.preRequestFormBarcode,
                    ...sample,
                    patient: sample.patient || { address: {} },
                    currentMedicalHistories: sample.currentMedicalHistories || [],
                    pastMedicalHistories: sample.pastMedicalHistories || [],
                    currentCancerHistories: [],
                    pastCancerHistories: [],
                    familyHistories: [],
                })
            } else {
                globalState.setNotificationMessage({ body: `依頼書コード：${samplesSearchForm.object.requestForm}は見つかりませんでした`, colorType: 'danger' })
            }
        }
    }, [samplesApi.loading])

    const showSubmitButton = () => {
        const f = form.object
        const a = f.patient?.address
        return (
            f.patient?.familyName &&
            f.patient?.familyNameKana &&
            f.patient?.givenName &&
            f.patient?.givenNameKana &&
            f.patient?.gender &&
            f.patient?.birthday &&
            a?.zipCode &&
            a?.prefecture &&
            a?.city &&
            a?.line &&
            f.productItemIds &&
            f.productItemIds.length > 0 &&
            f.collectionDate &&
            f.karteNumber &&
            f.smokingStatus &&
            f.doctorName &&
            f.agreement &&
            (f.hasCoupon === false || (f.hasCoupon === true && f.couponIds && f.couponIds.filter((id) => !!id).length > 0))
        )
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(inputApi.loading)
        if (inputApi.isSuccess()) {
            setShowConfirm(false)
            form.set({ patient: { address: {} }, familyHistories: [{}] })
            showTransportationApi.execute(Number(params.id))
        }
    }, [inputApi.loading])

    const vm = (): TransportationReportViewModel | undefined => {
        if (showTransportationApi.response) {
            return new TransportationReportViewModel(showTransportationApi.response)
        }
    }

    const isCompleted = (): boolean => {
        const ratio = vm()?.inputtedRatio()
        if (ratio) {
            return ratio >= 100
        } else {
            return false
        }
    }

    useEffectSkipFirst(() => {
        if (form.object.hasCoupon) {
            form.update((f) => {
                f.couponIds = [undefined]
                f.couponCodes = []
            })
        } else {
            form.update((f) => {
                f.couponIds = undefined
                f.couponCodes = []
            })
        }
    }, [form.object.hasCoupon])

    return (
        <>
            <ContentHeader
                headerTitle="検品 - 入力"
                left={
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                            props.history.goBack()
                        }}
                    >
                        戻る
                    </Button>
                }
            />
            <ContentBody>
                <div style={{ marginBottom: 20 }}>
                    <div>入力進捗</div>
                    <ProgressBar variant="warning" now={vm()?.inputtedRatio()} label={`${vm()?.inputtedRatio()}%`}></ProgressBar>
                </div>
                {isCompleted() ? (
                    <Alert variant="success">この搬送報告分の入力は完了しています</Alert>
                ) : (
                    <Flex>
                        <div style={{ flexGrow: 1 }}>
                            <div className="shadow" style={{ backgroundColor: 'white', padding: 20, borderRadius: 5, marginBottom: 50 }}>
                                <div>
                                    <Flex>
                                        <InputField
                                            form={form}
                                            attr="preRequestFormBarcode"
                                            required
                                            label="検査依頼書のバーコード"
                                            placeholder="スキャンしてEnterを押してください"
                                            onPressEnter={() => {
                                                samplesSearchForm.updateObject('requestForm', form.object.preRequestFormBarcode)
                                                form.updateObject('requestFormBarcode', form.object.preRequestFormBarcode)
                                                errorsSearchForm.updateObject('requestFormCode', form.object.requestFormBarcode)
                                            }}
                                            onlyHankaku
                                        />
                                    </Flex>
                                    {errorsApi.response.results.length > 0 && errorsApi.response.results.some((rf) => !rf.confirmationDate) ? (
                                        <Alert variant="danger">
                                            検査依頼書の不備が修正・確認されていません。先に依頼書不備(ID:{' '}
                                            {errorsApi.response.results.filter((rf) => !rf.confirmationDate)[0]!.id})の修正点を確認してください
                                            <a
                                                href={`/request_form_errors?id=${errorsApi.response.results.filter((rf) => !rf.confirmationDate)[0]!.id}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ marginLeft: 10 }}
                                            >
                                                リンク
                                            </a>
                                        </Alert>
                                    ) : form.object.requestFormBarcode &&
                                      showTransportationApi.response.draftRequestCodes?.includes(form.object.requestFormBarcode) ? (
                                        <>
                                            <PatientFormView form={patientForm} apiError={inputApi.apiError} />
                                            {form.object.patient?.isDuplicateChecked && (
                                                <div>
                                                    <div style={{ marginBottom: 20, padding: 10, backgroundColor: '#0801' }}>
                                                        <div style={{ marginBottom: 10 }}>現在治療中の病気</div>
                                                        {(() => {
                                                            const diseases = ['高血圧', '糖尿病', '脳梗塞', '心疾患', '子宮筋腫', 'COPD']
                                                            return (
                                                                <div style={{ marginBottom: 10 }}>
                                                                    <Flex style={{ marginBottom: 10 }}>
                                                                        {diseases.map((v, index) => {
                                                                            return (
                                                                                <CheckboxField
                                                                                    key={'current-medical-' + index}
                                                                                    attr={['currentMedicalHistories', index, 'disease']}
                                                                                    form={form}
                                                                                    value={v}
                                                                                />
                                                                            )
                                                                        })}
                                                                    </Flex>

                                                                    {form.object.currentCancerHistories?.length !== 0 && (
                                                                        <div style={{ padding: 10, backgroundColor: '#fafafa', marginBottom: 10 }}>
                                                                            {form.object.currentCancerHistories?.map((cch, index) => {
                                                                                return (
                                                                                    <Flex
                                                                                        alignItems="flex-end"
                                                                                        key={'current-medical-' + index}
                                                                                        style={{ marginBottom: 10 }}
                                                                                    >
                                                                                        <ChoiceField
                                                                                            label={'がん / 部位（例：肺、卵巣）'}
                                                                                            form={form}
                                                                                            tableName="current_medical_histories"
                                                                                            attr={['currentCancerHistories', index, 'part']}
                                                                                            style={{ marginBottom: 0 }}
                                                                                        />
                                                                                        {index + 1 === form.object.currentCancerHistories?.length && (
                                                                                            <CloseButton
                                                                                                style={{ marginBottom: 5 }}
                                                                                                onClick={() => {
                                                                                                    form.update((f) => {
                                                                                                        f.currentCancerHistories?.pop()
                                                                                                    })
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                    </Flex>
                                                                                )
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                    <div>
                                                                        <Button
                                                                            variant="outline-success"
                                                                            size="sm"
                                                                            onClick={() =>
                                                                                form.update((f) => {
                                                                                    f.currentCancerHistories?.push({ disease: 'がん' })
                                                                                })
                                                                            }
                                                                            style={{ width: '100%' }}
                                                                        >
                                                                            <FaPlus style={{ marginRight: 5 }} size={15} />
                                                                            「現在治療中の病気」にがんを追加
                                                                        </Button>
                                                                    </div>
                                                                    <div>
                                                                        <InputField
                                                                            form={form}
                                                                            attr="currentMedicalHistoryNote"
                                                                            label="自由入力欄"
                                                                            as="textarea"
                                                                            placeholder="チェック項目にない内容などはこちらに入力してください"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )
                                                        })()}
                                                    </div>
                                                    <div style={{ marginBottom: 20, padding: 10, backgroundColor: '#0081' }}>
                                                        <div style={{ marginBottom: 10 }}>過去にかかった病気</div>
                                                        {(() => {
                                                            const diseases = ['脳梗塞', '心疾患', '子宮筋腫']
                                                            return (
                                                                <div style={{ marginBottom: 10 }}>
                                                                    <Flex style={{ marginBottom: 10 }}>
                                                                        {diseases.map((v, index) => {
                                                                            return (
                                                                                <CheckboxField
                                                                                    key={'past-medical-' + index}
                                                                                    attr={['pastMedicalHistories', index, 'disease']}
                                                                                    form={form}
                                                                                    value={v}
                                                                                />
                                                                            )
                                                                        })}
                                                                    </Flex>
                                                                    {form.object.pastCancerHistories?.length !== 0 && (
                                                                        <div style={{ padding: 20, backgroundColor: '#fafafa', marginBottom: 10 }}>
                                                                            {form.object.pastCancerHistories?.map((cch, index) => {
                                                                                return (
                                                                                    <Flex
                                                                                        alignItems="flex-end"
                                                                                        key={'past-cancer-' + index}
                                                                                        style={{ marginBottom: 10 }}
                                                                                    >
                                                                                        <ChoiceField
                                                                                            label={'がん / 部位（例：肺、卵巣）'}
                                                                                            form={form}
                                                                                            tableName="past_medical_history"
                                                                                            attr={['pastCancerHistories', index, 'part']}
                                                                                            style={{ marginBottom: 0 }}
                                                                                        />
                                                                                        {index + 1 === form.object.pastCancerHistories?.length && (
                                                                                            <CloseButton
                                                                                                style={{ marginBottom: 5 }}
                                                                                                onClick={() => {
                                                                                                    form.update((f) => {
                                                                                                        f.pastCancerHistories?.pop()
                                                                                                    })
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                    </Flex>
                                                                                )
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                    <div>
                                                                        <Button
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            onClick={() =>
                                                                                form.update((f) => {
                                                                                    f.pastCancerHistories?.push({ disease: 'がん' })
                                                                                })
                                                                            }
                                                                            style={{ width: '100%' }}
                                                                        >
                                                                            <FaPlus style={{ marginRight: 5 }} size={15} />
                                                                            「過去にかかった病気」にがんを追加
                                                                        </Button>
                                                                    </div>

                                                                    <div>
                                                                        <InputField
                                                                            form={form}
                                                                            attr="pastMedicalHistoryNote"
                                                                            label="自由入力欄"
                                                                            as="textarea"
                                                                            placeholder="チェック項目にない内容などはこちらに入力してください"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )
                                                        })()}
                                                    </div>
                                                    <div style={{ marginBottom: 20, padding: 10, backgroundColor: '#ecfb0030', paddingBottom: 20 }}>
                                                        <div style={{ marginBottom: 10 }}>
                                                            家族歴（※同じ続柄で2つのがんがある場合は2行にして記載してください）
                                                        </div>
                                                        {form.object.familyHistories?.map((fh, index) => {
                                                            return (
                                                                <Flex key={'family-history-' + index} alignItems="flex-end">
                                                                    <ChoiceField
                                                                        form={form}
                                                                        attr={['familyHistories', index, 'relationship']}
                                                                        tableName="family_history"
                                                                        label="続柄"
                                                                    />
                                                                    <ChoiceField
                                                                        form={form}
                                                                        attr={['familyHistories', index, 'cancer']}
                                                                        label="がん"
                                                                        tableName="family_history"
                                                                    />
                                                                    {index + 1 === form.object.familyHistories?.length && (
                                                                        <CloseButton
                                                                            style={{ marginBottom: 22 }}
                                                                            onClick={() => {
                                                                                form.update((f) => {
                                                                                    f.familyHistories?.pop()
                                                                                })
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Flex>
                                                            )
                                                        })}
                                                        <Button
                                                            variant="outline-warning"
                                                            size="sm"
                                                            onClick={() =>
                                                                form.update((f) => {
                                                                    f.familyHistories?.push({})
                                                                })
                                                            }
                                                            style={{ width: '100%' }}
                                                        >
                                                            <FaPlus style={{ marginRight: 5 }} size={15} />
                                                            家族歴を追加
                                                        </Button>

                                                        <div>
                                                            <InputField
                                                                form={form}
                                                                attr="familyHistoryNote"
                                                                label="自由入力欄"
                                                                as="textarea"
                                                                placeholder="チェック項目にない内容などはこちらに入力してください"
                                                            />
                                                        </div>
                                                    </div>
                                                    <Flex style={{ marginBottom: 10 }}>
                                                        <div style={{ width: '50%', backgroundColor: '#a003', padding: 10 }}>
                                                            <SelectField
                                                                form={form}
                                                                attr="smokingStatus"
                                                                apiError={inputApi.apiError}
                                                                label="喫煙ステータス"
                                                                selectItems={SMOKING_STATUS_SELECT_ITEMS}
                                                                includeBlank
                                                                required
                                                            />
                                                            {form.object.smokingStatus === 'BEFORE' && (
                                                                <Flex>
                                                                    <InputField form={form} attr="smokingStartAge" type="number" label="喫煙開始（年齢）" />
                                                                    <InputField form={form} attr="smokingEndAge" type="number" label="喫煙終了（年齢）" />
                                                                </Flex>
                                                            )}
                                                            {form.object.smokingStatus === 'SMOKING' && (
                                                                <Flex>
                                                                    <InputField form={form} attr="smokingPerDay" type="number" label="1日あたりの平均喫煙数" />
                                                                    <InputField form={form} attr="smokingYears" type="number" label="喫煙年数" />
                                                                </Flex>
                                                            )}

                                                            <div>
                                                                <InputField form={form} attr="smokingNote" label="喫煙メモ" as="textarea" />
                                                            </div>
                                                        </div>
                                                        <div style={{ width: '50%', backgroundColor: '#4503', padding: 10 }}>
                                                            <SelectField
                                                                required
                                                                form={form}
                                                                attr="alcoholPerWeek"
                                                                selectItems={[
                                                                    { label: '飲む（日数不明）', value: -1 },
                                                                    { label: '1日', value: 1 },
                                                                    { label: '2日', value: 2 },
                                                                    { label: '3日', value: 3 },
                                                                    { label: '4日', value: 4 },
                                                                    { label: '5日', value: 5 },
                                                                    { label: '6日', value: 6 },
                                                                    { label: '7日', value: 7 },
                                                                ]}
                                                                apiError={inputApi.apiError}
                                                                label="1週間の飲酒日数"
                                                                includeBlank
                                                                blankLabel="飲まない"
                                                            />

                                                            <div>
                                                                <InputField form={form} attr="alcoholNote" label="飲酒メモ" as="textarea" />
                                                            </div>
                                                        </div>
                                                    </Flex>
                                                    {form.object.patient?.gender === 'FEMALE' && (
                                                        <div style={{ padding: '15px 10px', backgroundColor: '#ddd', marginBottom: 20 }}>
                                                            <div style={{ marginBottom: 10 }}>女性ステータス</div>
                                                            <Flex>
                                                                <CheckboxField form={form} attr="pregnancy" checkboxLabel="妊娠中" />
                                                                <CheckboxField form={form} attr="menstruation" checkboxLabel="生理中" />
                                                            </Flex>
                                                        </div>
                                                    )}
                                                    <Flex>
                                                        <InputField form={form} attr="doctorName" apiError={inputApi.apiError} label="提出医" required />
                                                        <InputField form={form} attr="karteNumber" apiError={inputApi.apiError} label="カルテ番号" required />
                                                    </Flex>
                                                    <div style={{ marginBottom: 10, marginTop: 10 }}>
                                                        <MultipleSelectField
                                                            id="product-items-check"
                                                            form={form}
                                                            attr="productItemIds"
                                                            apiError={inputApi.apiError}
                                                            label="製品"
                                                            required
                                                            selectItems={productItemsApi.response.results.map((p) => {
                                                                return { label: p.name, value: p.id! }
                                                            })}
                                                            horizontal
                                                            hideSearch
                                                            hideSelectAll
                                                        />
                                                    </div>
                                                    <DateField form={form} attr="collectionDate" apiError={inputApi.apiError} label="採取日" required />
                                                    <BooleanRadioField
                                                        form={form}
                                                        attr="hasCoupon"
                                                        label="優待（クーポン）の適用の有無"
                                                        positiveLabel="適用あり"
                                                        negativeLabel="適用なし"
                                                        required
                                                    />
                                                    {(form.object.couponIds?.length || 0 > 0) && (
                                                        <div style={{ padding: 20 }}>
                                                            {form.object.couponIds?.map((_, index) => {
                                                                return (
                                                                    <CouponSelectField
                                                                        key={'coupon-select-' + index}
                                                                        form={form}
                                                                        attr={['couponIds', index]}
                                                                        couponCodeAttr={['couponCodes', index]}
                                                                        coupons={couponsApi.response.results}
                                                                    />
                                                                )
                                                            })}
                                                        </div>
                                                    )}
                                                    <InputField form={form} attr={'note'} as="textarea" label="連絡事項"></InputField>
                                                    <CheckboxField form={form} attr={'agreement'} checkboxLabel="検査の同意書の署名を確認しました" required />
                                                    <CheckboxField
                                                        form={form}
                                                        attr={'followupStudyAgreement'}
                                                        checkboxLabel="フォローアップ研究への利用に同意しています"
                                                    />

                                                    {form.object.patient?.gender === 'MALE' &&
                                                        form.object.productItemIds?.includes(
                                                            productItemsApi.response.results.find((p) => p.name === '卵巣がん')?.id || 0
                                                        ) && (
                                                            <Alert style={{ marginTop: 10 }} variant="danger">
                                                                男性ですが卵巣がんが選択されています。医療機関に確認してください
                                                            </Alert>
                                                        )}
                                                    <Button
                                                        disabled={!showSubmitButton()}
                                                        onClick={() => {
                                                            inputApi.execute(form)
                                                        }}
                                                        style={{ width: '100%', marginTop: 20 }}
                                                    >
                                                        登録
                                                    </Button>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        form.object.requestFormBarcode && (
                                            <>
                                                <Alert variant="warning">依頼書コード：{form.object.requestFormBarcode}はまだ入庫されていません</Alert>
                                            </>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </Flex>
                )}
            </ContentBody>
        </>
    )
}

type CouponSelectFieldProps = {
    form: Form<SampleInputForm>
    attr: FormAttrType<SampleInputForm>
    couponCodeAttr: FormAttrType<SampleInputForm>
    coupons: Coupon[]
}
const CouponSelectField = (props: CouponSelectFieldProps) => {
    const globalState = useContext(GlobalStateContext)
    const couponSelectForm = useForm<{ hasCode: boolean; couponCode?: string; couponId?: number }>({ hasCode: true })
    const couponCodesSearchForm = useForm<CouponCodeSearchForm>({})
    const couponCodesApi = useFetchCouponCodesApi(couponCodesSearchForm)

    useEffectSkipFirst(() => {
        globalState.setLoading(couponCodesApi.loading)
        if (couponCodesApi.isSuccess() && couponCodesApi.response.results.length > 0) {
            couponSelectForm.updateObject('couponId', couponCodesApi.response.results[0].couponId)
        } else {
            globalState.setNotificationMessage({ body: 'クーポンが見つかりませんでした。', colorType: 'danger' })
        }
    }, [couponCodesApi.loading])

    useEffectSkipFirst(() => {
        if (!couponSelectForm.object.hasCode) {
            globalState.showConfirm('本当にクーポンコードなしですか？', () => {}, {
                onCancel: () => {
                    couponSelectForm.updateObject('hasCode', true)
                },
            })
        }

        couponSelectForm.update((f) => {
            delete f.couponCode
            delete f.couponId
        })
        props.form.updateObject(props.attr, undefined)
    }, [couponSelectForm.object.hasCode])

    useEffectSkipFirst(() => {
        props.form.updateObject(props.attr, couponSelectForm.object.couponId)
    }, [couponSelectForm.object.couponId])

    useEffectSkipFirst(() => {
        props.form.updateObject(props.couponCodeAttr, couponSelectForm.object.couponCode)
    }, [couponSelectForm.object.couponCode])

    return (
        <div>
            <BooleanRadioField form={couponSelectForm} attr="hasCode" positiveLabel="クーポンコードあり" negativeLabel="クーポンコードなし"></BooleanRadioField>
            {couponSelectForm.object.hasCode ? (
                <>
                    {couponSelectForm.object.couponId ? (
                        <Alert variant="success" style={{ marginTop: 10 }}>
                            「{props.coupons.find((c) => c.id === couponSelectForm.object.couponId)!.name}」が適用されます（クーポンコード：{' '}
                            {couponSelectForm.object.couponCode}）
                        </Alert>
                    ) : (
                        <>
                            <Flex alignItems="flex-end">
                                <InputField
                                    form={couponSelectForm}
                                    attr="couponCode"
                                    placeholder="バーコードリーダーで読み取る"
                                    label={'クーポンコードを入力してください'}
                                    onPressEnter={() => {}}
                                    style={{ marginBottom: 0 }}
                                />
                                <Button
                                    style={{ width: 130 }}
                                    variant="secondary"
                                    onClick={() => {
                                        couponCodesSearchForm.updateObject('value', couponSelectForm.object.couponCode)
                                    }}
                                >
                                    コード検索
                                </Button>
                            </Flex>
                        </>
                    )}
                </>
            ) : (
                <SelectField
                    form={couponSelectForm}
                    attr="couponId"
                    selectItems={props.coupons
                        .filter((c) => !c.hasCode)
                        .map((c) => {
                            return { label: c.name!, value: c.id! }
                        })}
                    label="優待（クーポン）を選択してください"
                    includeBlank
                />
            )}
        </div>
    )
}

export default withRouter(InputPage)
